import { appScrollTo, getAppScrollTop } from '@hooks/appScroll/helpers';
import { useCallback, useContext, useEffect, useLayoutEffect } from 'react';
import {
  Location,
  UNSAFE_DataRouterContext,
  UNSAFE_DataRouterStateContext,
  useLocation,
  useNavigation,
} from 'react-router';
import { AppScrollRestorationProps } from './types';

const STORAGE_KEY = 'app-scroll-positions';

// eslint-disable-next-line immutable/no-let
let savedPositions: Record<string, number> = {};

export const AppScrollRestoration = ({ getKey }: AppScrollRestorationProps) => {
  const router = useContext(UNSAFE_DataRouterContext)?.router;
  const { restoreScrollPosition, preventScrollReset } =
    useContext(UNSAFE_DataRouterStateContext) || {};
  const location = useLocation();
  const navigation = useNavigation();

  const getLocationKey = useCallback((loc: Location) => (getKey ? getKey(loc) : loc.key), [getKey]);

  useEffect(() => {
    // eslint-disable-next-line immutable/no-mutation
    window.history.scrollRestoration = 'manual';
    return () => {
      // eslint-disable-next-line immutable/no-mutation
      window.history.scrollRestoration = 'auto';
    };
  }, []);

  useEffect(() => {
    const onPageHide = () => {
      if (navigation.state === 'idle') savedPositions[getLocationKey(location)] = getAppScrollTop();
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(savedPositions));
      } catch {
        // storage unavailable (private mode / quota) – restoration just won't survive a reload
      }
      // eslint-disable-next-line immutable/no-mutation
      window.history.scrollRestoration = 'auto';
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [navigation.state, location, getLocationKey]);

  useLayoutEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) savedPositions = JSON.parse(stored) as Record<string, number>;
    } catch {
      // ignore corrupted / unavailable storage
    }
  }, []);

  useLayoutEffect(
    () =>
      router?.enableScrollRestoration(savedPositions, getAppScrollTop, (loc) =>
        getLocationKey(loc),
      ),
    [router, getLocationKey],
  );

  useLayoutEffect(() => {
    if (restoreScrollPosition === false) return;
    if (typeof restoreScrollPosition === 'number') {
      appScrollTo({ top: restoreScrollPosition });
      return;
    }
    if (location.hash) {
      try {
        const element = document.getElementById(decodeURIComponent(location.hash.slice(1)));
        if (element) {
          element.scrollIntoView();
          return;
        }
      } catch {
        // not a decodable element id – fall through to the default behaviour
      }
    }
    if (preventScrollReset === true) return;
    appScrollTo({ top: 0 });
  }, [location, restoreScrollPosition, preventScrollReset]);

  return null;
};
